import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Distribution, TaxComponents } from '../../data/types';
import { calculateDistributionLines, DEFAULT_COMPONENTS } from '../../domain/distributionCalc';
import { addDays, addMonths, DEMO_TODAY } from '../../lib/format';
import { newId } from '../../lib/random';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { PageHeader } from '../../ui/primitives';
import ComponentsEditor from './ComponentsEditor';
import DistributionPreview from './DistributionPreview';

const STEPS = ['Period and rate', 'Tax components', 'Calculate and review'];
const PERIODS_PER_YEAR = { MONTHLY: 12, QUARTERLY: 4, SEMI_ANNUAL: 2, ANNUAL: 1 };

export default function DistributionWizardPage() {
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [fundId, setFundId] = useState(params.get('fund') ?? scope.funds[0]?.id ?? '');
  const fund = scope.fundById.get(fundId);
  const classes = scope.classes.filter((item) => item.fundId === fundId);
  const [classId, setClassId] = useState(classes[0]?.id ?? '');
  const monthly = fund?.distributionFrequency === 'MONTHLY';
  const [periodStart, setPeriodStart] = useState(monthly ? '2026-10-01' : '2026-10-01');
  const [periodEnd, setPeriodEnd] = useState(monthly ? '2026-10-31' : '2026-12-31');
  const [paymentDate, setPaymentDate] = useState(monthly ? '2026-11-10' : '2027-01-15');
  const unitClass = scope.classById.get(classId);
  const perYear = PERIODS_PER_YEAR[fund?.distributionFrequency ?? 'MONTHLY'];
  const [annualPct, setAnnualPct] = useState(String(unitClass?.targetReturnPct ?? 8));
  const [components, setComponents] = useState<TaxComponents>(DEFAULT_COMPONENTS);
  const [drpEnabled, setDrpEnabled] = useState(fund?.basis === 'NAV_UNITS');

  const centsPerUnit =
    Math.round(((Number(annualPct) || 0) / perYear) * (unitClass?.unitPrice ?? 1) * 10_000) /
    10_000;
  const lines = useMemo(
    () =>
      calculateDistributionLines(
        {
          classId,
          recordDate: periodEnd > DEMO_TODAY ? DEMO_TODAY : periodEnd,
          centsPerUnit,
          components,
          drpEnabled,
        },
        state.ledger,
        scope.holders,
      ),
    [classId, periodEnd, centsPerUnit, components, drpEnabled, state.ledger, scope.holders],
  );
  const componentTotal = Object.values(components).reduce((total, value) => total + value, 0);

  const build = (): Distribution => ({
    id: newId('ds'),
    fundId,
    classId,
    periodStart,
    periodEnd,
    recordDate: periodEnd,
    paymentDate,
    centsPerUnit,
    status: 'DRAFT',
    components,
    createdBy: scope.user.name,
    lines,
  });

  const changeFund = (next: string) => {
    setFundId(next);
    const nextFund = scope.fundById.get(next);
    const nextClass = scope.classes.find((item) => item.fundId === next);
    setClassId(nextClass?.id ?? '');
    setAnnualPct(String(nextClass?.targetReturnPct ?? 8));
    setDrpEnabled(nextFund?.basis === 'NAV_UNITS');
    if (nextFund?.distributionFrequency === 'MONTHLY') {
      setPeriodStart('2026-10-01');
      setPeriodEnd('2026-10-31');
      setPaymentDate('2026-11-10');
    } else {
      setPeriodStart('2026-10-01');
      setPeriodEnd(addDays(addMonths('2026-10-01', 3), -1));
      setPaymentDate('2027-01-15');
    }
  };

  return (
    <>
      <PageHeader
        title="New distribution"
        subtitle="Entitlements are calculated from units held on the record date."
      />
      <Stepper activeStep={step} sx={{ mb: 3 }} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {step === 0 && (
        <Paper variant="outlined" sx={{ p: 3, maxWidth: 760 }}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <TextField
              select
              label="Fund"
              value={fundId}
              onChange={(event) => changeFund(event.target.value)}
            >
              {scope.funds
                .filter((item) => item.status !== 'WOUND_UP')
                .map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.code} · {item.name}
                  </MenuItem>
                ))}
            </TextField>
            <TextField
              select
              label="Class"
              value={classId}
              onChange={(event) => setClassId(event.target.value)}
            >
              {classes.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Period start"
              type="date"
              value={periodStart}
              onChange={(event) => setPeriodStart(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Period end (record date)"
              type="date"
              value={periodEnd}
              onChange={(event) => setPeriodEnd(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Payment date"
              type="date"
              value={paymentDate}
              onChange={(event) => setPaymentDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Distribution rate (% p.a.)"
              type="number"
              value={annualPct}
              onChange={(event) => setAnnualPct(event.target.value)}
              helperText={`= ${centsPerUnit} cents per unit for this ${fund?.distributionFrequency.toLowerCase().replace('_', '-')} period`}
            />
            {fund?.basis === 'NAV_UNITS' && (
              <TextField
                select
                label="Distribution reinvestment"
                value={drpEnabled ? 'on' : 'off'}
                onChange={(event) => setDrpEnabled(event.target.value === 'on')}
              >
                <MenuItem value="on">DRP active (reinvest for electing holders)</MenuItem>
                <MenuItem value="off">Pay everyone in cash</MenuItem>
              </TextField>
            )}
          </Box>
          {periodEnd > DEMO_TODAY && (
            <Alert severity="info" sx={{ mt: 2 }}>
              The record date is in the future, so this preview uses today&apos;s register.
              Recalculate on the record date before submitting.
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              disabled={!classId || !Number(annualPct)}
              onClick={() => setStep(1)}
            >
              Next
            </Button>
          </Box>
        </Paper>
      )}
      {step === 1 && (
        <Paper variant="outlined" sx={{ p: 3, maxWidth: 760 }}>
          <ComponentsEditor value={components} onChange={setComponents} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={() => setStep(0)}>Back</Button>
            <Button
              variant="contained"
              disabled={Math.abs(componentTotal - 1) > 0.0001}
              onClick={() => setStep(2)}
            >
              Calculate
            </Button>
          </Box>
        </Paper>
      )}
      {step === 2 && (
        <>
          {lines.length === 0 ? (
            <Alert severity="warning">No holders in this class on the record date.</Alert>
          ) : (
            <DistributionPreview distribution={build()} />
          )}
          <Paper
            variant="outlined"
            sx={{ p: 2, mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
          >
            <Button onClick={() => setStep(1)}>Back</Button>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              Save as draft to recalculate later, or submit for approval now.
            </Typography>
            <Button
              variant="outlined"
              disabled={!lines.length}
              onClick={() => {
                dispatch({ type: 'DISTRIBUTION_SAVE', distribution: build() });
                toast('Draft saved');
                navigate('/manager/distributions');
              }}
            >
              Save draft
            </Button>
            <Button
              variant="contained"
              disabled={!lines.length}
              onClick={() => {
                const distribution = build();
                dispatch({ type: 'DISTRIBUTION_SAVE', distribution });
                dispatch({ type: 'DISTRIBUTION_SUBMIT', id: distribution.id });
                toast('Distribution submitted for approval');
                navigate(`/manager/distributions/${distribution.id}`);
              }}
            >
              Submit for approval
            </Button>
          </Paper>
        </>
      )}
    </>
  );
}
